import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
	RegularizationBuilderMode,
	RegularizationSource,
} from '@/features/regularization/hooks/useRegularizationTracking';

/** El modal de regularización se abre desde seis lugares distintos y el
 * constructor no puede distinguirlos si el estado solo dice "abierto". La
 * apertura viaja con su origen para que el funnel sepa qué superficie lo
 * disparó; `null` es cerrado, así no hay un booleano que pueda contradecirlo. */
export type RegularizationModalState = {
	mode: RegularizationBuilderMode;
	source: RegularizationSource;
};

/** Modal de NPS (t-015). Viaja con su disparador para que los eventos
 * distingan el auto-show (elegibilidad) del deep link `?nps=1` del journey;
 * `null` es cerrado. */
export type NpsModalTrigger = 'auto' | 'deeplink';
export type NpsModalState = { trigger: NpsModalTrigger };

interface UIState {
	modalSkeletonLoading: string | null;
	npsModal: NpsModalState | null;
	sidebarIsExpanded: boolean;
	mobileMenuHiders: number;
	regularizationModal: RegularizationModalState | null;
	showModalApprovalDeclarations: boolean;
	showModalPendingDeclarationWithPrecalculate: boolean;
	showModalRating: boolean;
	showReferralsWelcomeModal: boolean;
	isLoadingBasicInformation: boolean;
	showWrappedModal: boolean;
	showNotifications: boolean;
	showReferralsModal: boolean;
	dashboardExplorationOptIn: boolean;
	showSatConnectionDetailsModal: boolean;
	showFiscalDrawer: boolean;
}

const initialState: UIState = {
	modalSkeletonLoading: null,
	npsModal: null,
	sidebarIsExpanded: true,
	mobileMenuHiders: 0,
	regularizationModal: null,
	showModalApprovalDeclarations: false,
	showModalPendingDeclarationWithPrecalculate: false,
	showModalRating: false,
	showReferralsWelcomeModal: false,
	isLoadingBasicInformation: true,
	showWrappedModal: false,
	showNotifications: false,
	showReferralsModal: false,
	dashboardExplorationOptIn: false,
	showSatConnectionDetailsModal: false,
	showFiscalDrawer: false,
};

export const uiSlice = createSlice({
	name: 'ui',
	initialState,
	reducers: {
		setModalSkeletonLoading: (state, action: PayloadAction<string | null>) => {
			state.modalSkeletonLoading = action.payload;
		},
		setSidebarIsExpanded: (state, action: PayloadAction<boolean>) => {
			state.sidebarIsExpanded = action.payload;
		},
		hideMobileMenu: (state) => {
			state.mobileMenuHiders += 1;
		},
		restoreMobileMenu: (state) => {
			state.mobileMenuHiders = Math.max(0, state.mobileMenuHiders - 1);
		},
		openRegularizationModal: (
			state,
			action: PayloadAction<RegularizationModalState>,
		) => {
			state.regularizationModal = action.payload;
		},
		closeRegularizationModal: (state) => {
			state.regularizationModal = null;
		},
		setShowModalApprovalDeclarations: (
			state,
			action: PayloadAction<boolean>,
		) => {
			state.showModalApprovalDeclarations = action.payload;
		},
		setShowModalPendingDeclarationWithPrecalculate: (
			state,
			action: PayloadAction<boolean>,
		) => {
			state.showModalPendingDeclarationWithPrecalculate = action.payload;
		},
		setShowModalRating: (state, action: PayloadAction<boolean>) => {
			state.showModalRating = action.payload;
		},
		openNpsModal: (state, action: PayloadAction<NpsModalState>) => {
			state.npsModal = action.payload;
		},
		closeNpsModal: (state) => {
			state.npsModal = null;
		},
		setShowReferralsWelcomeModal: (state, action: PayloadAction<boolean>) => {
			state.showReferralsWelcomeModal = action.payload;
		},
		setIsLoadingBasicInformation: (state, action: PayloadAction<boolean>) => {
			state.isLoadingBasicInformation = action.payload;
		},
		setShowWrappedModal: (state, action: PayloadAction<boolean>) => {
			state.showWrappedModal = action.payload;
		},
		setShowNotifications: (state, action: PayloadAction<boolean>) => {
			state.showNotifications = action.payload;
		},
		setShowReferralsModal: (state, action: PayloadAction<boolean>) => {
			state.showReferralsModal = action.payload;
		},
		setDashboardExplorationOptIn: (state, action: PayloadAction<boolean>) => {
			state.dashboardExplorationOptIn = action.payload;
		},
		setShowSatConnectionDetailsModal: (
			state,
			action: PayloadAction<boolean>,
		) => {
			state.showSatConnectionDetailsModal = action.payload;
		},
		setShowFiscalDrawer: (state, action: PayloadAction<boolean>) => {
			state.showFiscalDrawer = action.payload;
		},
	},
});

export const {
	setModalSkeletonLoading,
	setSidebarIsExpanded,
	hideMobileMenu,
	restoreMobileMenu,
	openRegularizationModal,
	closeRegularizationModal,
	setShowModalApprovalDeclarations,
	setShowModalPendingDeclarationWithPrecalculate,
	setShowModalRating,
	openNpsModal,
	closeNpsModal,
	setShowReferralsWelcomeModal,
	setIsLoadingBasicInformation,
	setShowWrappedModal,
	setShowNotifications,
	setShowReferralsModal,
	setDashboardExplorationOptIn,
	setShowSatConnectionDetailsModal,
	setShowFiscalDrawer,
} = uiSlice.actions;
